/**
 * LotSmith — Metric card.
 *
 * Used on the dashboard for the four top-of-page KPIs. Keeps rendering
 * deterministic for tabular-num alignment — formats money with decimal.js
 * precision so the digits always line up across cards.
 */
import Decimal from "decimal.js";

export type MetricFormat = "usd" | "usd-signed" | "raw";
export type Tone = "default" | "profit" | "loss" | "warning" | "info" | "muted";

export interface MetricCardProps {
  label: string;
  value: number;
  format: MetricFormat;
  intent?: Tone;
  valueTone?: Tone;
  delta?: string;
  deltaTone?: Tone;
}

export function MetricCard({
  label,
  value,
  format,
  intent = "default",
  valueTone = "default",
  delta,
  deltaTone = "muted",
}: MetricCardProps) {
  return (
    <div className={`metric metric--${intent}`}>
      <div className="metric__label">{label}</div>
      <div className={`metric__value num tone-${valueTone}`}>
        {formatValue(value, format)}
      </div>
      {delta && <div className={`metric__delta tone-${deltaTone}`}>{delta}</div>}
    </div>
  );
}

function formatValue(value: number, format: MetricFormat): string {
  const d = new Decimal(value);
  switch (format) {
    case "usd": {
      // A negative value here is rare (blown account → negative equity) but
      // when it happens we still want "-$1,234.56", not "$-1,234.56".
      const sign = d.isNegative() ? "-" : "";
      const abs = d.abs().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${sign}$${abs}`;
    }
    case "usd-signed": {
      const sign = d.isNegative() ? "-" : d.isPositive() ? "+" : "";
      const abs = d.abs().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${sign}$${abs}`;
    }
    case "raw":
      return d.toString();
  }
}
